import { FunctionComponent, MouseEvent, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { CSSTransition } from 'react-transition-group';
import { useTheme } from 'styled-components';
import { CardContent, Ellipsis, IconButton } from 'components/elements';
import { usePageBlur } from 'hooks';
import { DialogCard, DialogCardHeader, DialogScrollArea, StyledDialog } from './Dialog.styled';
import { DialogProps } from './types';

export const Dialog: FunctionComponent<DialogProps> = props => {
  const { children, open, title, onClose } = props;

  // Get reference to the parent element of the side panel
  const parentElement = useMemo(() => document.getElementById('root') ?? document.body, []);

  // Retrieve page content blur helpers
  const { pageBlur, pageUnblur } = usePageBlur();

  // References to main and dialog elements
  const dialogRef = useRef<HTMLDivElement>(null);
  const ref = useRef<HTMLDivElement>(null);

  // Retrieve application theme
  const theme = useTheme();

  /**
   * Handle clicks on the overlay element
   *
   * @param {MouseEvent} event Event object
   */
  const handleMouseDown = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      // Only close the side panel when clicking outside the panel ref
      if (!dialogRef.current?.contains(event.target as Node)) {
        onClose && onClose();
      }
    },
    [onClose],
  );

  return createPortal(
    <CSSTransition
      in={open}
      mountOnEnter={true}
      nodeRef={ref}
      timeout={parseInt(theme.transition.fast)}
      unmountOnExit={true}
      onEnter={pageBlur}
      onExit={pageUnblur}
    >
      <StyledDialog ref={ref} onMouseDown={handleMouseDown}>
        <DialogCard ref={dialogRef}>
          <DialogCardHeader>
            <Ellipsis>{title}</Ellipsis>
            <IconButton icon="close" size="small" variant="transparent" onClick={onClose} />
          </DialogCardHeader>
          <DialogScrollArea>
            <CardContent>{children}</CardContent>
          </DialogScrollArea>
        </DialogCard>
      </StyledDialog>
    </CSSTransition>,
    parentElement,
  );
};
